import { cn } from "@/lib/utils";
import { panelRadiusClass } from "../chat/branding";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
} from "@/components/molecules/responsive-dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { downloadBlob } from "@/features/utils/blob";
import { api, fetchServer } from "@/features/utils/fetch-server";
import { useDateFormat, usePublicTranslation } from "@/features/utils/i18n";
import { CalendarIcon, ClockIcon, DownloadSimpleIcon, MapPinIcon, UsersIcon, VideoCameraIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { toast } from "sonner";
import {
  type BookingEvent,
  useCancelBookingEvent,
  useConfirmBookingEvent,
} from "../../hooks/use-public-booking";
import { LoginDialog } from "./LoginDialog";

interface ViewCalendarEventProps {
  publicId: string;
  event: BookingEvent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Detect if user is on iOS or macOS
const isApplePlatform = () => {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || /Macintosh/.test(ua);
};

export function ViewCalendarEvent({
  publicId,
  event,
  open,
  onOpenChange,
}: ViewCalendarEventProps) {
  const { t } = usePublicTranslation();
  const { format } = useDateFormat();
  const confirmMutation = useConfirmBookingEvent(publicId);
  const cancelMutation = useCancelBookingEvent(publicId);
  const [showDescription, setShowDescription] = useState(false);
  // A booking made on declared contacts (the calendar's declarative mode) is changed only
  // once the visitor proved their phone: the action waits behind the sign-in, then runs.
  const [pendingAction, setPendingAction] = useState<
    "confirm" | "cancel" | null
  >(null);

  if (!event) return null;

  const needsSignIn = (error: unknown, action: "confirm" | "cancel") => {
    if ((error as { code?: string })?.code !== "sign_in_required") return false;
    setPendingAction(action);
    return true;
  };

  // The conversation's language, not the browser's: a visitor who switched the
  // chat to English read their appointment in French, or the other way round.
  const formatDate = (timestamp: number) => format(timestamp, "PPPP");
  const formatTime = (timestamp: number) => format(timestamp, "p");

  const handleConfirm = async () => {
    try {
      await confirmMutation.mutateAsync(event.id);
      toast.success(
        t("calendar_event.confirmed_success"),
      );
      onOpenChange(false);
    } catch (error) {
      if (needsSignIn(error, "confirm")) return;
      console.error("Failed to confirm event:", error);
      toast.error(
        t("calendar_event.confirm_error"),
      );
    }
  };

  const handleCancel = async () => {
    try {
      await cancelMutation.mutateAsync(event.id);
      toast.success(
        t("calendar_event.cancel_success"),
      );
      onOpenChange(false);
    } catch (error) {
      if (needsSignIn(error, "cancel")) return;
      console.error("Failed to cancel event:", error);
      toast.error(t("calendar_event.cancel_error"));
    }
  };

  const handleDownload = async () => {
    try {
      // `fetchServer` rather than `api.get` — the reply is a file, not JSON.
      const response = await fetchServer(
        `/api/sessions/v1/public/${publicId}/booking/${event.id}/ics`,
      );

      const blob = await response.blob();
      downloadBlob(blob, `event-${event.id}.ics`);
    } catch (error) {
      console.error("Failed to download ICS:", error);
      toast.error(
        t("calendar_event.download_error"),
      );
    }
  };

  const handleGoogleCalendar = async () => {
    try {
      const data = await api.get<{ url?: string }>(
        `/api/sessions/v1/public/${publicId}/booking/${event.id}/google`,
      );
      if (data.url) {
        window.open(data.url, "_blank");
      }
    } catch (error) {
      console.error("Failed to get Google Calendar URL:", error);
      toast.error(
        t("calendar_event.google_error"),
      );
    }
  };

  const handleOutlookCalendar = async () => {
    try {
      const data = await api.get<{ url?: string }>(
        `/api/sessions/v1/public/${publicId}/booking/${event.id}/outlook`,
      );
      if (data.url) {
        window.open(data.url, "_blank");
      }
    } catch (error) {
      console.error("Failed to get Outlook Calendar URL:", error);
      toast.error(
        t("calendar_event.outlook_error"),
      );
    }
  };

  const isPending = confirmMutation.isPending || cancelMutation.isPending;

  return (
    <>
      <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
        <ResponsiveDialogContent className={cn("sm:max-w-[525px]", panelRadiusClass)}>
          <ResponsiveDialogHeader>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold">{event.summary}</h2>
              </div>
            </div>
          </ResponsiveDialogHeader>

          <div className="p-4 space-y-4">
            {/* Date & Time */}
            <div className="flex items-start gap-3">
              <ClockIcon className="size-5 text-muted-foreground mt-0.5" />
              <div className="space-y-1">
                <p className="font-medium">{formatDate(event.start_time)}</p>
                {!event.is_all_day && (
                  <p className="text-sm text-muted-foreground">
                    {formatTime(event.start_time)} - {formatTime(event.end_time)}
                  </p>
                )}
                {event.is_all_day && (
                  <p className="text-sm text-muted-foreground">
                    {t("calendar_event.all_day")}
                  </p>
                )}
                {event.is_recurring && event.recurrence_rule && (
                  <p className="text-xs text-muted-foreground">
                    🔁 {t("calendar_event.recurring")}
                  </p>
                )}
              </div>
            </div>

            {/* Video meeting — the appointment happens here, so it comes before
                the address and is a button rather than a line of text. */}
            {event.conference_url && (
              <div className="flex items-start gap-3">
                <VideoCameraIcon className="size-5 text-muted-foreground mt-0.5" />
                <div>
                  <a
                    href={event.conference_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm font-medium text-primary underline underline-offset-4"
                  >
                    {t("calendar_event.join_video")}
                  </a>
                </div>
              </div>
            )}

            {/* Location */}
            {event.location && event.location !== event.conference_url && (
              <div className="flex items-start gap-3">
                <MapPinIcon className="size-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm">{event.location}</p>
                </div>
              </div>
            )}

            {/* Attendees */}
            {event.attendees && event.attendees.length > 0 && (
              <div className="flex items-start gap-3">
                <UsersIcon className="size-5 text-muted-foreground mt-0.5" />
                <div className="space-y-1">
                  {event.attendees.map((attendee, idx) => (
                    <p key={idx} className="text-sm">
                      {attendee.name}
                      {/* Only show email if present (exclude performers and optionally customer's own email) */}
                      {attendee.email && (
                        <span className="text-muted-foreground ml-2">
                          {attendee.email}
                        </span>
                      )}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* Description */}
            {event.description && (
              <>
                <Separator />
                <div className="space-y-2">
                  {!showDescription ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowDescription(true)}
                      className="h-auto px-0 text-sm text-muted-foreground hover:text-foreground"
                    >
                      {t("calendar_event.show_description")}
                    </Button>
                  ) : (
                    <>
                      <p className="text-sm whitespace-pre-wrap">
                        {event.description}
                      </p>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowDescription(false)}
                        className="h-auto px-0 text-sm text-muted-foreground hover:text-foreground"
                      >
                        {t("calendar_event.hide_description")}
                      </Button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          <ResponsiveDialogFooter className="flex-col sm:flex-row gap-2">
            <div className="flex gap-2 flex-1 w-full">
              {isApplePlatform() ? (
                <Button
                  variant="outline"
                  disabled={isPending}
                  onClick={handleDownload}
                  className="w-full sm:w-auto sm:flex-initial"
                >
                  <DownloadSimpleIcon className="size-4" />
                  {t("calendar_event.add_to_calendar")}
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    disabled={isPending}
                    onClick={handleGoogleCalendar}
                    className="flex-1 sm:flex-initial"
                  >
                    <CalendarIcon className="size-4" />
                    {t("calendar_event.google_calendar")}
                  </Button>
                  <Button
                    variant="outline"
                    disabled={isPending}
                    onClick={handleOutlookCalendar}
                    className="flex-1 sm:flex-initial"
                  >
                    <CalendarIcon className="size-4" />
                    {t("calendar_event.outlook_calendar")}
                  </Button>
                </>
              )}
            </div>
            <div className="flex gap-3 w-full sm:w-auto">
              {event.status !== "confirmed" && (
                <Button
                  onClick={handleConfirm}
                  disabled={isPending}
                  variant="default"
                  className="flex-1 sm:flex-initial"
                >
                  {confirmMutation.isPending
                    ? t("calendar_event.confirming")
                    : t("calendar_event.confirm")}
                </Button>
              )}
              <Button
                onClick={handleCancel}
                disabled={isPending}
                variant="destructive"
                className="flex-1 sm:flex-initial"
                confirm={
                  t("calendar_event.cancel_confirm")
                }
              >
                {cancelMutation.isPending
                  ? t("calendar_event.canceling")
                  : t("calendar_event.cancel_event")}
              </Button>
            </div>
          </ResponsiveDialogFooter>
        </ResponsiveDialogContent>
      </ResponsiveDialog>

      {/* The code first, then the confirmation or the cancellation the visitor asked for. */}
      <LoginDialog
        publicId={publicId}
        open={!!pendingAction}
        onOpenChange={(next) => {
          if (!next) setPendingAction(null);
        }}
        onSuccess={() => {
          const action = pendingAction;
          setPendingAction(null);
          void (action === "confirm" ? handleConfirm() : handleCancel());
        }}
      />
    </>
  );
}
